import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Sparkles, Sun, Zap, Gift, Loader2, AlertTriangle, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { key: 'novita', label: 'Novità', Icon: Sparkles, desc: 'Nuovo arrivo in collezione', sel: 'border-blue-600 bg-blue-50 ring-1 ring-blue-300' },
  { key: 'inizio_stagione', label: 'Inizio Stagione', Icon: Sun, desc: 'La stagione arriva: prenota in anticipo', sel: 'border-amber-500 bg-amber-50 ring-1 ring-amber-300' },
  { key: 'lastminute', label: 'Last Minute', Icon: Zap, desc: 'Urgenza su date vicine — periodo obbligatorio', sel: 'border-red-500 bg-red-50 ring-1 ring-red-300' },
  { key: 'offerta_speciale', label: 'Offerta Speciale', Icon: Gift, desc: 'Promo dedicata — sconto o prezzo obbligatorio', sel: 'border-emerald-600 bg-emerald-50 ring-1 ring-emerald-300' },
];

export default function NewsletterCampaignDialog({ property, open, onOpenChange, onLinkSaved, onCreated }) {
  const navigate = useNavigate();
  const token = localStorage.getItem('hw_token');
  const H = { headers: { Authorization: `Bearer ${token}` } };

  const [type, setType] = useState('novita');
  const [offerStart, setOfferStart] = useState('');
  const [offerEnd, setOfferEnd] = useState('');
  const [discount, setDiscount] = useState('');
  const [price, setPrice] = useState('');
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setType('novita'); setOfferStart(''); setOfferEnd(''); setDiscount(''); setPrice('');
      setLink(property?.holidayworld_url || '');
    }
  }, [open, property]);

  const showOffer = type === 'lastminute' || type === 'offerta_speciale';

  const create = async () => {
    if (type === 'lastminute' && (!offerStart || !offerEnd)) {
      toast.error('Il Last Minute richiede il periodo (dal/al)'); return;
    }
    if (type === 'offerta_speciale' && !discount && !price) {
      toast.error("L'Offerta Speciale richiede uno sconto % o un prezzo"); return;
    }
    setBusy(true);
    try {
      const cleanLink = (link || '').trim();
      if (cleanLink !== (property?.holidayworld_url || '').trim()) {
        await axios.put(`${API}/properties/${property.id}`, { holidayworld_url: cleanLink }, H);
        if (onLinkSaved) onLinkSaved(cleanLink);
      }
      const res = await axios.post(`${API}/newsletters/from-property`, {
        property_id: property.id,
        type,
        offer_start: offerStart || null,
        offer_end: offerEnd || null,
        discount_percent: discount ? parseFloat(discount) : null,
        offer_price: price ? parseFloat(price) : null,
      }, H);
      toast.success('Bozza newsletter creata');
      onOpenChange(false);
      if (onCreated) onCreated(res.data.newsletter);
      else navigate(`/newsletter?open=${res.data.newsletter.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore creazione newsletter');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="newsletter-campaign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-800" /> Crea Newsletter — {property?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs text-slate-500">Tipo di campagna</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {TYPES.map(({ key, label, Icon, desc, sel }) => (
                <button key={key} type="button" onClick={() => setType(key)}
                  className={`text-left border rounded-lg p-3 transition-colors ${type === key ? sel : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid={`nl-type-${key}`}>
                  <div className="flex items-center gap-2 font-semibold text-sm text-slate-800">
                    <Icon className="h-4 w-4" /> {label}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1 leading-snug">{desc}</p>
                </button>
              ))}
            </div>
          </div>

          {showOffer && (
            <div className="border rounded-lg p-3 bg-slate-50 space-y-3" data-testid="nl-offer-fields">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-500">
                    Periodo dal {type === 'lastminute' && <span className="text-red-500">*</span>}
                  </Label>
                  <Input type="date" value={offerStart} onChange={(e) => setOfferStart(e.target.value)}
                    data-testid="nl-offer-start" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">
                    al {type === 'lastminute' && <span className="text-red-500">*</span>}
                  </Label>
                  <Input type="date" value={offerEnd} onChange={(e) => setOfferEnd(e.target.value)}
                    data-testid="nl-offer-end" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-500">
                    Sconto % {type === 'offerta_speciale' && <span className="text-red-500">*</span>}
                  </Label>
                  <Input type="number" min="1" max="90" placeholder="es. 20" value={discount}
                    onChange={(e) => setDiscount(e.target.value)} data-testid="nl-discount-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">oppure Prezzo da €/notte</Label>
                  <Input type="number" min="1" placeholder="es. 89" value={price}
                    onChange={(e) => setPrice(e.target.value)} data-testid="nl-price-input" />
                </div>
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs text-slate-500">Link casa su holidayworld.it (CTA della newsletter)</Label>
            <Input placeholder="https://www.holidayworld.it/..." value={link}
              onChange={(e) => setLink(e.target.value)} data-testid="nl-link-input" />
            {!link.trim() && (
              <p className="flex items-center gap-1 text-[11px] text-amber-700 mt-1">
                <AlertTriangle className="h-3 w-3" /> Senza link il pulsante porterà alla homepage di holidayworld.it
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="nl-cancel-btn">
              Annulla
            </Button>
            <Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={create} disabled={busy}
              data-testid="nl-create-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}
              Crea bozza
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
